import React, { useMemo, useState } from 'react';
import { getTenantToday } from '../../utils/dateUtils';
import axios from 'axios';
import styled from 'styled-components';

import { DateField } from '../shared/DateField';

/**
 * V360/V364 — unified tabular-tape import. Drop a servicer strat/remittance CSV →
 * AI proposes a header→canonical mapping (deterministic fallback) → analyst reviews
 * & edits → deterministic loader applies it → surveillance populates + provenance
 * audit. AI maps columns only, never per-row data. Shared shape for RMBS/ABF/CMBS.
 */

const Backdrop = styled.div`position: fixed; inset: 0; background: rgba(2,6,23,.72); z-index: 1320; display: grid; place-items: center;`;
const Modal = styled.div`width: min(820px, 95vw); max-height: 90vh; overflow: auto; background: #0f172a; border: 1px solid rgba(148,163,184,.22); border-radius: 12px; padding: 24px; color: #e2e8f0;`;
const Row = styled.div`display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 12px;`;
const Field = styled.label`display: flex; flex-direction: column; gap: 5px; font-size: 12px; color: #94a3b8;
  input, select { background: rgba(15,23,42,.8); border: 1px solid rgba(148,163,184,.25); border-radius: 7px; padding: 8px 10px; color: #e2e8f0; font-size: 13px; }
`;
const Button = styled.button<{ $primary?: boolean }>`
  border: 1px solid ${p => p.$primary ? 'rgba(56,189,248,.5)' : 'rgba(148,163,184,.3)'};
  background: ${p => p.$primary ? 'rgba(14,116,144,.32)' : 'rgba(30,41,59,.6)'};
  color: ${p => p.$primary ? '#bae6fd' : '#cbd5e1'};
  border-radius: 8px; padding: 9px 14px; font-size: 12px; font-weight: 700; cursor: pointer;
  &:disabled { opacity: .45; cursor: not-allowed; }
`;
const SectionTitle = styled.h4`font-size: 12px; color: #cbd5e1; margin: 20px 0 8px; text-transform: uppercase; letter-spacing: .05em;`;
const Note = styled.div<{ $tone?: string }>`font-size: 12px; padding: 8px 11px; border-radius: 7px; margin: 8px 0;
  background: ${p => p.$tone === 'ok' ? 'rgba(34,197,94,.12)' : p.$tone === 'warn' ? 'rgba(251,191,36,.12)' : 'rgba(56,189,248,.1)'};
  color: ${p => p.$tone === 'ok' ? '#86efac' : p.$tone === 'warn' ? '#fcd34d' : '#bae6fd'};`;
const MapGrid = styled.div`display: grid; grid-template-columns: 160px 1fr; gap: 8px 12px; align-items: center; margin-bottom: 6px;
  .canon { font-size: 12px; color: #cbd5e1; font-family: ui-monospace, monospace; }
  select { background: rgba(15,23,42,.8); border: 1px solid rgba(148,163,184,.25); border-radius: 7px; padding: 7px 9px; color: #e2e8f0; font-size: 13px; }
`;

// Must mirror TapeMappingService.STRAT_FIELDS
const CANON_FIELDS: { key: string; label: string }[] = [
  { key: 'band', label: 'band (bucket label)' },
  { key: 'balance', label: 'balance (UPB)' },
  { key: 'balancePct', label: 'balancePct (% of pool)' },
  { key: 'numLoans', label: 'numLoans' },
  { key: 'waCouponPct', label: 'waCouponPct' },
  { key: 'waFico', label: 'waFico' },
  { key: 'waLtvPct', label: 'waLtvPct' },
  { key: 'delinquencyPct', label: 'delinquencyPct' },
];

const todayIso = () => getTenantToday();

interface Props { poolId: number; dealName: string; onClose: () => void; onLoaded?: () => void; }

const RmbsTapeImportModal: React.FC<Props> = ({ poolId, dealName, onClose, onLoaded }) => {
  const [dimension, setDimension] = useState('FICO');
  const [originator, setOriginator] = useState('');
  const [asOfDate, setAsOfDate] = useState(todayIso());
  const [fileName, setFileName] = useState('');
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [proposal, setProposal] = useState<any | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [result, setResult] = useState<any | null>(null);

  const headerLine = useMemo(() => (csv.split(/\r?\n/).find(l => l.trim().length > 0) || '').trim(), [csv]);
  const sourceCols: string[] = proposal?.sourceColumns || [];

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const r = new FileReader();
    r.onload = () => { setCsv(String(r.result || '')); setProposal(null); setResult(null); };
    r.readAsText(f);
  };

  const propose = async () => {
    setBusy(true); setErr(''); setResult(null);
    try {
      const res = await axios.post(`/api/credit/rmbs/pools/${poolId}/tape/propose`,
        { tapeKind: 'RMBS_STRAT', dimension, headerLine });
      setProposal(res.data);
      setMapping({ ...(res.data?.proposedMapping || {}) });
    } catch (e: any) {
      setErr(e.response?.data?.error || e.message || 'Propose failed');
    } finally { setBusy(false); }
  };

  const load = async () => {
    setBusy(true); setErr('');
    try {
      const res = await axios.post(`/api/credit/rmbs/pools/${poolId}/tape/load`, {
        tapeKind: 'RMBS_STRAT', dimension, originator: originator || dealName,
        asOfDate, fileName: fileName || 'tape.csv', csv, mapping,
      });
      setResult(res.data);
      onLoaded?.();
    } catch (e: any) {
      setErr(e.response?.data?.error || e.message || 'Load failed');
    } finally { setBusy(false); }
  };

  return (
    <Backdrop onMouseDown={onClose}>
      <Modal onMouseDown={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Import Tape — {dealName}</h3>
        <Note>AI maps the file's columns to the canonical schema; you confirm before any rows load. The approved mapping is saved per originator and reused next time.</Note>

        <Row>
          <Field>Dimension
            <select value={dimension} onChange={e => { setDimension(e.target.value); setProposal(null); }}>
              {['FICO', 'LTV', 'RATE', 'STATE', 'VINTAGE', 'DTI', 'OCCUPANCY', 'PRODUCT'].map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field>Originator / servicer
            <input value={originator} onChange={e => setOriginator(e.target.value)} placeholder={dealName} />
          </Field>
        </Row>
        <Row>
          <Field>As-of date
            <DateField value={asOfDate ?? ''} onChange={iso => setAsOfDate(iso)} />
          </Field>
          <Field>Tape file (.csv)
            <input type="file" accept=".csv,text/csv" onChange={onFile} />
          </Field>
        </Row>

        {csv && (
          <>
            <SectionTitle>Detected header</SectionTitle>
            <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: '#94a3b8', background: 'rgba(15,23,42,.6)', padding: '8px 10px', borderRadius: 7, overflowX: 'auto' }}>{headerLine || '(no header)'}</div>
          </>
        )}

        {err && <Note $tone="warn">{err}</Note>}

        {!proposal && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button $primary disabled={busy || !headerLine} onClick={propose}>{busy ? 'Proposing…' : 'Propose mapping'}</Button>
          </div>
        )}

        {proposal && (
          <>
            <SectionTitle>Review mapping</SectionTitle>
            <Note $tone={proposal.existingTemplate ? 'ok' : undefined}>
              {proposal.existingTemplate ? '✓ Reusing the saved template for this originator.' : (proposal.aiUsed ? 'AI-proposed mapping (review & edit below).' : 'Deterministic mapping (AI unavailable).')}
            </Note>
            {CANON_FIELDS.map(f => (
              <MapGrid key={f.key}>
                <span className="canon">{f.label}</span>
                <select value={mapping[f.key] || ''} onChange={e => setMapping(m => ({ ...m, [f.key]: e.target.value }))}>
                  <option value="">— not mapped —</option>
                  {sourceCols.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </MapGrid>
            ))}
            {!mapping.band && <Note $tone="warn">A <b>band</b> column is required to load.</Note>}

            {result ? (
              <Note $tone="ok">Loaded {result.rowsLoaded}/{result.rowsTotal} rows{result.rowsRejected ? ` (${result.rowsRejected} rejected)` : ''} into {result.dimension} stratification. Provenance recorded (template #{result.templateId}).</Note>
            ) : null}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 16 }}>
              <Button onClick={() => { setProposal(null); setResult(null); }}>Back</Button>
              <div style={{ display: 'flex', gap: 10 }}>
                <Button onClick={onClose}>Close</Button>
                <Button $primary disabled={busy || !mapping.band} onClick={load}>{busy ? 'Loading…' : (result ? 'Re-load' : 'Load tape')}</Button>
              </div>
            </div>
          </>
        )}
      </Modal>
    </Backdrop>
  );
};

export default RmbsTapeImportModal;
